import React, { useState } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
  Alert,
  Spinner,
} from "react-bootstrap";
import { Eye, EyeSlash, CaretDownFill } from "react-bootstrap-icons";

import BG_URL from "../img/SG.png";
import LOGO_URL from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_SIGNUP = `${API_BASE}/signup`;

// "HARRY potter" -> "Harry Potter" (also after hyphens and apostrophes)
const toNameCase = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/(^|[\s\-'])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());

export default function RegisterPage() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneExt: "+65",
    phone: "",
    password: "",
    licenseExpiredDate: "",
    badgeNumber: "",
    pdpaConsent: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showPw, setShowPw] = useState(false);

  const PHONE_CODES = [
    { code: "+65", label: "SG (+65)" },
    { code: "+95", label: "MM (+95)" },
    { code: "+60", label: "MY (+60)" },
    { code: "+66", label: "TH (+66)" },
    { code: "+62", label: "ID (+62)" },
    { code: "+63", label: "PH (+63)" },
    { code: "+84", label: "VN (+84)" },
    { code: "+91", label: "IN (+91)" },
    { code: "+86", label: "CN (+86)" },
    { code: "+1", label: "USA (+1)" },
    { code: "+44", label: "UK (+44)" },
    { code: "+61", label: "AU (+61)" },
  ];

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onlyDigits = (s = "") => s.replace(/\D+/g, "");
  const isValidEmail = (s = "") => /^\S+@\S+\.\S+$/.test(String(s).trim());

  const validate = () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      return "Please enter your first and family name.";
    }
    if (!isValidEmail(form.email)) {
      return "Please enter a valid email address.";
    }
    if (!form.password || form.password.length < 6) {
      return "Password must be at least 6 characters.";
    }

    const phoneDigits = onlyDigits(form.phone);
    if (!phoneDigits || phoneDigits.length < 8) {
      return "Phone number must contain at least 8 digits.";
    }

    const badgeDigits = onlyDigits(form.badgeNumber);
    if (badgeDigits.length !== 4) {
      return "Badge number must be exactly 4 digits.";
    }

    if (!form.pdpaConsent) {
      return "Please consent to the PDPA and Privacy Policy to continue.";
    }

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    const payload = {
      username: form.email.trim().toLowerCase(),
      firstName: toNameCase(form.firstName.trim()),
      lastName: toNameCase(form.lastName.trim()),
      password: form.password,
      countryCode: form.phoneExt,
      phoneNumber: onlyDigits(form.phone).trim(),
      tgExpiryDate: form.licenseExpiredDate || null,
      badgeNo: onlyDigits(form.badgeNumber).trim(),
      name: toNameCase(`${form.firstName} ${form.lastName}`.trim()),
      phoneFull: `${form.phoneExt} ${onlyDigits(form.phone)}`.trim(),
      pdpaConsent: form.pdpaConsent ? 1 : 0, // 👈 if backend wants it
    };

    try {
      setSubmitting(true);

      const token = localStorage.getItem("authToken");

      const { data } = await axios.post(ENDPOINT_SIGNUP, payload, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      if (data?.success) {
        setSuccessMsg(
          data?.message ||
            "Verification email has been sent. Please check your inbox."
        );
        setForm((f) => ({ ...f, password: "" }));
      } else {
        setError(
          data?.message ||
            "Unable to sign up right now. Please try again shortly."
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (err?.response?.status === 401
          ? "The username is already used. Please kindly try to login."
          : "Server error. Please try again later.");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isSGExt = (ext) => /^(?:\+?65|SG)$/i.test(String(ext || "").trim());

  const ymd = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const MIN_DATE = ymd(tomorrow);

  return (
    <div
      className="login-wrap"
      style={{
        minHeight: "100vh",
        width: "100%",
        backgroundImage: `url(${BG_URL})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        paddingTop: "70px",
        paddingBottom: "40px",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.10) 35%, rgba(255,255,255,0.30) 100%)",
        }}
      />

      <style>{`
        .register-card {
          width: 600px;
          max-width: 100%;
          background: rgba(255, 255, 255, 0.78);
          backdrop-filter: blur(8px);
          border-radius: 22px;
          padding: 1px 24px;
          box-shadow: 0 6px 28px rgba(0, 0, 0, 0.12);
          border: 1px solid rgba(0,0,0,0.06);
        }
        .name-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        @media (max-width: 576px) {
          .name-row { grid-template-columns: 1fr; }
        }
        .phone-row {
          display: grid;
          grid-template-columns: 160px 1fr;
          gap: 10px;
          position: relative;
        }
        @media (max-width: 480px) {
          .phone-row { grid-template-columns: 1fr; }
        }
        .ext-wrap {
          position: relative;
          width: 100%;
        }
        .ext-select {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-color: #fff;
          border: 1px solid #ced4da;
          border-radius: 8px;
          padding-right: 2.25rem;
        }
        .ext-caret {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
        }
        .eye-toggle {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          border: none;
          background: transparent;
          padding: 0 4px;
          cursor: pointer;
          line-height: 1;
        }
        .pr-eye {
          padding-right: 40px;
        }
        .btn-register {
          padding: 12px 16px;
          font-weight: 700;
          letter-spacing: .6px;
        }
        .text-holder {
          border-radius: 8px;
        }
        .back-link {
          text-decoration: none;
        }
      `}</style>

      <Container style={{ zIndex: 1 }}>
        <Row className="justify-content-center text-center">
          <Col xs={12}>
            <img
              src={LOGO_URL}
              alt="TMIS Logo"
              style={{
                height: 100,
                objectFit: "contain",
                marginBottom: 18,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
          </Col>

          <Col xs={12} md={10} lg={8} xl={6} xxl={5}>
            <Card className="register-card mx-auto custom-font">
              <Card.Body style={{ padding: "20px 20px" }}>
                <h3
                  className="fw-bold text-center"
                  style={{ letterSpacing: 1.1, marginBottom: 22, fontSize: 25 }}
                >
                  REGISTER
                </h3>

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {successMsg && (
                  <Alert variant="success" className="mb-3">
                    {successMsg}
                  </Alert>
                )}

                <Form onSubmit={onSubmit} style={{ textAlign: "left" }}>
                  <div className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <div className="name-row">
                      <Form.Control
                        size="lg"
                        type="text"
                        placeholder="Given / First name"
                        value={form.firstName}
                        onChange={(e) => setField("firstName", toNameCase(e.target.value))}
                        className="text-holder"
                      />
                      <Form.Control
                        size="lg"
                        type="text"
                        placeholder="Surname / Family name"
                        value={form.lastName}
                        onChange={(e) => setField("lastName", toNameCase(e.target.value))}
                        className="text-holder"
                      />
                    </div>
                  </div>

                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      size="lg"
                      type="email"
                      placeholder="Enter Your Email Address"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value.toLowerCase())}
                      className="text-holder"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="phone">
                    <Form.Label>Phone</Form.Label>
                    <div className="phone-row">
                      <div className="ext-wrap">
                        <Form.Select
                          size="lg"
                          value={form.phoneExt}
                          onChange={(e) => setField("phoneExt", e.target.value)}
                          className="ext-select"
                          aria-label="Phone extension"
                        >
                          {PHONE_CODES.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.label}
                            </option>
                          ))}
                        </Form.Select>
                        <CaretDownFill
                          size={18}
                          color="#555"
                          className="ext-caret"
                          aria-hidden="true"
                        />
                      </div>

                      <Form.Control
                        size="lg"
                        type="tel"
                        inputMode="numeric"
                        pattern={
                          isSGExt(form.phoneExt) ? "\\d{8}" : "\\d{8,15}"
                        }
                        maxLength={isSGExt(form.phoneExt) ? 8 : 15}
                        placeholder={
                          isSGExt(form.phoneExt)
                            ? "Enter 8-digit SG number"
                            : "Enter your phone number"
                        }
                        value={form.phone}
                        onChange={(e) => {
                          const only = e.target.value.replace(/\D/g, "");
                          const limited = isSGExt(form.phoneExt)
                            ? only.slice(0, 8)
                            : only.slice(0, 15);
                          setField("phone", limited);
                        }}
                        className="text-holder"
                        aria-label="Phone number"
                        required
                        isInvalid={
                          isSGExt(form.phoneExt) &&
                          form.phone.length > 0 &&
                          form.phone.length !== 8
                        }
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-2" controlId="password">
                    <Form.Label>Password </Form.Label><span className="text-muted" style={{fontSize: "12px"}}> (Minimum of 6 alphanumeric characters)</span>
                    <div className="position-relative">
                      <Form.Control
                        size="lg"
                        type={showPw ? "text" : "password"}
                        placeholder="Enter your password"
                        value={form.password}
                        onChange={(e) => setField("password", e.target.value)}
                        className="text-holder pr-eye"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="eye-toggle"
                        onClick={() => setShowPw((s) => !s)}
                        aria-label={showPw ? "Hide password" : "Show password"}
                        title={showPw ? "Hide password" : "Show password"}
                      >
                        {showPw ? <Eye size={18} /> : <EyeSlash size={18} />}
                      </button>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="licenseDate">
                    <Form.Label>TG License Expiry Date</Form.Label>
                    <InputGroup size="md">
                      <Form.Control
                        type="date"
                        min={MIN_DATE}
                        value={form.licenseExpiredDate}
                        onChange={(e) => {
                          const v = e.target.value;
                          setField(
                            "licenseExpiredDate",
                            v && v < MIN_DATE ? MIN_DATE : v
                          );
                        }}
                        className="text-holder"
                      />
                    </InputGroup>
                    <span className="text-muted" style={{fontSize: "12px"}}>Enter your TG Licence Expiry date</span>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="badge">
                    <Form.Label>Badge Number</Form.Label>

                    <div
                      className="badge-underscore-container"
                      onClick={() =>
                        document.getElementById("badgeHidden")?.focus()
                      }
                    >
                      {[0, 1, 2, 3].map((i) => (
                        <div className="badge-slot" key={i}>
                          {form.badgeNumber[i] || "_"}
                        </div>
                      ))}
                    </div>

                    {/* Real hidden input */}
                    <input
                      id="badgeHidden"
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      value={form.badgeNumber}
                      onChange={(e) => {
                        const digits = onlyDigits(e.target.value).slice(0, 4);
                        setField("badgeNumber", digits);
                      }}
                      onBlur={(e) => {
                        const digits = onlyDigits(e.target.value);
                        setField(
                          "badgeNumber",
                          digits ? digits.padStart(4, "0") : ""
                        );
                      }}
                      className="badge-real-input"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="pdpaConsent">
                    <Form.Check
                      type="checkbox"
                      required
                      checked={form.pdpaConsent}
                      onChange={(e) =>
                        setField("pdpaConsent", e.target.checked)
                      }
                      label={
                        <span style={{ fontSize: "13px", lineHeight: 1.4 }}>
                          I acknowledge that I have read and understood TMIS’s
                          Personal Data Protection Policy and consent to the
                          collection, use, and disclosure of my personal data
                          for purposes related to LEARN + DO. Click{" "}
                          <a
                            href="https://tmis.edu.sg/images/TMIS-PDPA-Notice-for-Customer.pdf"
                            target="_blank"
                            rel="noreferrer"
                          >
                            here
                          </a>{" "}
                        </span>
                      }
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      className="btn-register custom-font"
                      type="submit"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Registering...
                        </>
                      ) : (
                        "REGISTER"
                      )}
                    </Button>
                  </div>
                </Form>

                <div className="text-center mt-3">
                  <a href="/" className="back-link">
                    Back to Login
                  </a>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
